import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Intake from "@/pages/Intake";
import Today from "@/pages/Today";
import Decisions from "@/pages/Decisions";
import Review from "@/pages/Review";
import Financial from "@/pages/Financial";
import AskSarge from "@/pages/AskSarge";
import CommandBoard from "@/pages/CommandBoard";
import Memory from "@/pages/Memory";
import FilesTools from "@/pages/FilesTools";
import Settings from "@/pages/Settings";
import Operator from "@/pages/Operator";
import Legal from "@/pages/Legal";
import Press from "@/pages/Press";
import BriefConfirm from "@/pages/BriefConfirm";
import BriefUnsubscribe from "@/pages/BriefUnsubscribe";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import SetPassword from "@/pages/SetPassword";
import TermsGate from "@/components/TermsGate";

function FullLoader() {
  return (
    <div className="min-h-screen bg-wr-bg flex items-center justify-center" data-testid="app-loader">
      <div className="flex flex-col items-center gap-3">
        <span className="h-8 w-8 border-2 border-wr-border border-t-wr-red rounded-full animate-spin" />
        <span className="wr-label">Establishing session…</span>
      </div>
    </div>
  );
}

function RequireFounder({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (user.role === "operator") return <Navigate to="/operator" replace />;
  return <TermsGate>{children}</TermsGate>;
}

function RequireOnboarding({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "operator") return <Navigate to="/operator" replace />;
  return <TermsGate>{children}</TermsGate>;
}

function RequireOperator({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "operator") return <Navigate to="/app/brief" replace />;
  return children;
}

function Shell({ children }) {
  return <AppShell>{children}</AppShell>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" theme="dark" richColors />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/legal" element={<Navigate to="/legal/terms" replace />} />
            <Route path="/legal/:doc" element={<Legal />} />
            <Route path="/press/sarge-founding-beta" element={<Press />} />
            <Route path="/press" element={<Navigate to="/press/sarge-founding-beta" replace />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/brief/confirm" element={<BriefConfirm />} />
            <Route path="/brief/unsubscribe" element={<BriefUnsubscribe />} />
            <Route
              path="/onboarding"
              element={<Navigate to="/intake" replace />}
            />
            <Route
              path="/intake"
              element={
                <RequireOnboarding>
                  <Intake />
                </RequireOnboarding>
              }
            />
            <Route
              path="/operator"
              element={
                <RequireOperator>
                  <Operator />
                </RequireOperator>
              }
            />
            <Route path="/app" element={<Navigate to="/app/today" replace />} />
            <Route path="/app/brief" element={<Navigate to="/app/today" replace />} />
            <Route
              path="/app/today"
              element={
                <RequireFounder>
                  <Shell>
                    <Today />
                  </Shell>
                </RequireFounder>
              }
            />
            <Route
              path="/app/decisions"
              element={
                <RequireFounder>
                  <Shell>
                    <Decisions />
                  </Shell>
                </RequireFounder>
              }
            />
            <Route
              path="/app/review"
              element={
                <RequireFounder>
                  <Shell>
                    <Review />
                  </Shell>
                </RequireFounder>
              }
            />
            <Route
              path="/app/financial"
              element={
                <RequireFounder>
                  <Shell>
                    <Financial />
                  </Shell>
                </RequireFounder>
              }
            />
            <Route
              path="/app/sarge"
              element={
                <RequireFounder>
                  <Shell>
                    <AskSarge />
                  </Shell>
                </RequireFounder>
              }
            />
            <Route
              path="/app/sarge/:id"
              element={
                <RequireFounder>
                  <Shell>
                    <AskSarge />
                  </Shell>
                </RequireFounder>
              }
            />
            <Route
              path="/app/command"
              element={
                <RequireFounder>
                  <Shell>
                    <CommandBoard />
                  </Shell>
                </RequireFounder>
              }
            />
            <Route
              path="/app/memory"
              element={
                <RequireFounder>
                  <Shell>
                    <Memory />
                  </Shell>
                </RequireFounder>
              }
            />
            <Route
              path="/app/tools"
              element={
                <RequireFounder>
                  <Shell>
                    <FilesTools />
                  </Shell>
                </RequireFounder>
              }
            />
            <Route
              path="/app/settings"
              element={
                <RequireFounder>
                  <Shell>
                    <Settings />
                  </Shell>
                </RequireFounder>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
